import Select from "react-select";
import { useQuery } from "@tanstack/react-query";
import { type Device } from "../store/admin/devicesStore";
import { allDevicesQueryOptions } from "../features/devices/queries";
import { useCompaniesStore, useDisableCRUD } from "../store/admin/companiesStore";
import { getCustomSelectStyles } from "../Layouts/admin/shared";
import useIsMonitor from "../hooks/useIsMonitor";

interface DeviceOption {
  value: string;
  label: string;
}

interface InfiniteDeviceSelectProps {
  value: string[];
  onChange: (values: string[]) => void;
  isDarkMode: boolean;
  themeClasses: {
    cardBorder: string;
    text: string;
  };
  disabled?: boolean;
  placeholder?: string;
}

const formatDeviceOption = (device: Device): DeviceOption => ({
  value: device.id,
  label: `${device.identifier} ${device.vehicle?.name ? `(${device.vehicle.name})` : ""}`,
});

// Reusa la cache de devices de TanStack (`["devices","all",cid]`). Solo consulta
// cuando hay companyId en el store (evita fallback a companyUser al limpiar el
// selector del header en flujos reseller).
function useFilteredReportDevices() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const { data, isLoading } = useQuery({
    ...allDevicesQueryOptions(companyId),
  });
  const { isMonitor, devices: monitorDeviceIds } = useIsMonitor();
  const devices = companyId ? ((data ?? []) as Device[]) : [];
  const filtered =
    isMonitor && Array.isArray(monitorDeviceIds) && monitorDeviceIds.length > 0
      ? devices.filter((d) => monitorDeviceIds.includes(d.id))
      : devices;
  return { filtered, isLoading: !!companyId && isLoading };
}

export default function InfiniteDeviceSelect({
  value,
  onChange,
  isDarkMode,
  themeClasses,
  disabled = false,
  placeholder = "Seleccionar equipos",
}: InfiniteDeviceSelectProps) {
  const disableWithoutCompany = useDisableCRUD();
  const isDisabled = disabled || disableWithoutCompany;
  const { filtered, isLoading } = useFilteredReportDevices();

  const options = filtered.map(formatDeviceOption);
  const selectedOptions = filtered
    .filter((device) => value?.includes(device.id))
    .map(formatDeviceOption);

  return (
    <Select
      id="devices"
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      isDisabled={isDisabled}
      isLoading={isLoading && filtered.length === 0}
      options={options}
      value={selectedOptions}
      onChange={(selected: any) =>
        onChange(selected?.map((opt: any) => opt.value) || [])
      }
      placeholder={
        disableWithoutCompany ? "Selecciona una compañía primero" : placeholder
      }
      className={`min-w-60 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
      styles={getCustomSelectStyles(isDarkMode) as any}
    />
  );
}

export function useReportDeviceIds() {
  const { filtered } = useFilteredReportDevices();
  return {
    loadedDeviceIds: filtered.map((d) => d.id),
    totalDevices: filtered.length,
    loadedCount: filtered.length,
  };
}
